import { ReactNode, useCallback, useEffect, useLayoutEffect, useRef, useState } from "react";
import { useChat } from "@ai-sdk/react";
import type {
  ChatOnDataCallback,
  ChatOnErrorCallback,
  ChatOnFinishCallback,
  ChatOnToolCallCallback,
} from "ai";
import { useShallow } from "zustand/react/shallow";
import { noop } from "lodash-es";

import type { AiChatSession } from "components/AiChat/types";
import AiChatMessages from "components/AiChat/Messages";
import AiChatComposer, { AiChatComposerRef } from "components/AiChat/Composer";
import Badge from "ds/components/Badge";
import Box from "ds/components/Box";
import { Flash, GraduationHat, MessageBubble, Plus } from "components/icons/generated";
import AssistantPanelLayoutHeaderActions from "components/AssistantPanel/Layout/Header/Actions";
import AssistantPanelChatTermsOverlay from "components/AssistantPanel/Chat/TermsOverlay";
import LoadingIndicator from "ds/components/LoadingIndicator";
import ButtonIcon from "ds/components/ButtonIcon";
import Button from "ds/components/Button";
import { IconComponent } from "types/Icon";
import { useContainerBreakpoint } from "hooks/useContainerBreakpoint";
import useDetectMobile from "hooks/useDetectMobile";
import useIsAssistantPageMode from "components/AssistantPanel/hooks/useIsAssistantPageMode";

import useAssistantPanelStore from "../../Layout/useStore";
import { ASSISTANT_PANEL_CONTAINER } from "../../Layout/constants";
import { AssistantPanelView } from "../../Layout/types";
import useAssistantPanelAnalytics from "../../analytics/useAnalytics";
import { AssistantPanelLearnModeSource } from "../../analytics/types";
import AssistantPanelLayoutHeader from "../../Layout/Header";
import ChatExamples from "../Examples";
import AssistantPanelChatWelcome from "../Welcome";
import {
  chatCallbacks,
  ConversationConflictError,
  createNewChatPlaceholder,
  isChatStatusBusy,
  notifyChatError,
} from "../helpers";
import { HISTORY_SIDE_PANEL_BREAKPOINT } from "../constants";
import { ChatUIMessage, ChatMode } from "../types";
import useChatStore from "../store/useStore";
import useToolApproval from "../hooks/useToolApproval";
import { executeToolCall } from "../../tools/executeToolCall";
import AssistantPanelHistoryDropdown from "../History/Dropdown";
import AssistantPanelChatConversationActions from "../ConversationActions";
import styles from "./styles.module.css";

type AssistantPanelChatContentProps = {
  toolIcons: Record<string, IconComponent>;
  onFirstMessage: (message: string) => void;
  onNewChat: () => void;
  conversations: AiChatSession[];
  conversationsLoading: boolean;
  conversationLoading: boolean;
  onSelectChat: (chatId: string) => void;
  onDeleteChat: (chatId: string) => void;
  onRefetchConversations: () => void;
  onReloadChat: () => void;
  composerToolbarSlot?: ReactNode;
  composerFooterSlot?: ReactNode;
  beforeComposer?: ReactNode;
  showTermsOverlay?: boolean;
};

const AssistantPanelChatContent = ({
  toolIcons,
  onFirstMessage,
  onNewChat,
  conversations,
  conversationsLoading,
  conversationLoading,
  onSelectChat,
  onDeleteChat,
  onRefetchConversations,
  onReloadChat,
  composerToolbarSlot,
  composerFooterSlot,
  beforeComposer,
  showTermsOverlay,
}: AssistantPanelChatContentProps) => {
  const {
    chat,
    pendingMessage,
    composerDraft,
    focusKey,
    setConversationVersion,
    setBudgetUsage,
    clearPendingMessage,
    clearComposerDraft,
    setMode,
    mode,
    approvalMode,
    mcpFilteredTools,
    frontendTools,
  } = useChatStore(
    useShallow((state) => ({
      chat: state.chat,
      pendingMessage: state.pendingMessage,
      composerDraft: state.composerDraft,
      focusKey: state.focusKey,
      setConversationVersion: state.setConversationVersion,
      setBudgetUsage: state.setBudgetUsage,
      clearPendingMessage: state.clearPendingMessage,
      clearComposerDraft: state.clearComposerDraft,
      setMode: state.setMode,
      mode: state.mode,
      approvalMode: state.approvalMode,
      mcpFilteredTools: state.mcpFilteredTools,
      frontendTools: state.frontendTools,
    }))
  );

  const chatId = chat?.id;

  const addToolOutputRef = useRef<
    ((params: { toolCallId: string; tool: string; output: unknown }) => void) | undefined
  >(undefined);

  const toolAbortControllersRef = useRef<Set<AbortController>>(new Set());

  const runToolCall = useCallback(
    async (toolCall: { toolCallId: string; toolName: string; input: unknown }) => {
      const addToolOutput = addToolOutputRef.current;
      if (!addToolOutput) {
        return;
      }

      const controller = new AbortController();
      toolAbortControllersRef.current.add(controller);

      try {
        await executeToolCall({
          mcpTools: mcpFilteredTools,
          frontendTools,
          toolCall,
          addToolOutput,
          abortSignal: controller.signal,
        });
      } finally {
        toolAbortControllersRef.current.delete(controller);
      }
    },
    [mcpFilteredTools, frontendTools]
  );

  const [newChat] = useState(createNewChatPlaceholder);

  const {
    messages,
    sendMessage,
    setMessages,
    status,
    stop,
    addToolOutput,
    addToolApprovalResponse,
    error,
  } = useChat<ChatUIMessage>({ chat: chat ?? newChat });

  const onToolCall: ChatOnToolCallCallback<ChatUIMessage> = async ({ toolCall }) => {
    const { toolCallId, toolName, input } = toolCall;

    if (useChatStore.getState().mcpFilteredTools[toolName]?.needsApproval) {
      return;
    }

    await runToolCall({ toolCallId, toolName, input });
  };

  const onData: ChatOnDataCallback<ChatUIMessage> = (message) => {
    if (message.type === "data-conversation-version") {
      setConversationVersion(message.data.version);
    }

    if (message.type === "data-conversation-description") {
      onRefetchConversations();
    }

    if (message.type === "data-budget-usage") {
      setBudgetUsage({
        tokensResetAt: message.data.tokensResetAt,
        totalTokens: message.data.totalTokens,
        usedTokens: message.data.usedTokens,
        usedTokensCurrentSession: message.data.usedTokensCurrentTurn,
      });
    }
  };

  const onFinish: ChatOnFinishCallback<ChatUIMessage> = ({ messages, isAbort }) => {
    toolAbortControllersRef.current.clear();

    if (isAbort) {
      const lastMessage = messages[messages.length - 1];
      const hasIncompleteReasoning = lastMessage?.parts.some(
        (part) => part.type === "reasoning" && !part.providerMetadata?.anthropic?.signature
      );
      const hasIncompleteToolCalls = lastMessage?.parts.some(
        (part) =>
          part.type.startsWith("tool-") &&
          "state" in part &&
          part.state !== "output-available" &&
          part.state !== "output-error" &&
          part.state !== "output-denied"
      );

      if (hasIncompleteReasoning || hasIncompleteToolCalls) {
        setMessages(messages.slice(0, -1));
      }

      onReloadChat();
    }
  };

  const onError: ChatOnErrorCallback = (error) => {
    const type = error instanceof ConversationConflictError ? "conflict" : "stream";
    notifyChatError(error, type);
    onReloadChat();
    console.error("Chat error:", error);
  };

  useLayoutEffect(() => {
    chatCallbacks.current = { onToolCall, onData, onFinish, onError };
  });

  const handleStop = useCallback(() => {
    toolAbortControllersRef.current.forEach((controller) => controller.abort());
    toolAbortControllersRef.current.clear();
    stop();
  }, [stop]);

  const { handleApproveTool, handleRejectTool } = useToolApproval({
    approvalMode,
    messages,
    runToolCall,
    addToolApprovalResponse,
  });

  const handleSendMessage = useCallback(
    (message: string) => {
      sendMessage({ text: message });
    },
    [sendMessage]
  );

  useEffect(() => {
    addToolOutputRef.current = addToolOutput;
  }, [addToolOutput]);

  const isAssistantPageMode = useIsAssistantPageMode();
  const isMobile = useDetectMobile();

  const handleSubmit = useCallback(
    (message: string) => {
      // A chat the user writes in on the page follows them into the drawer when they leave.
      // Not on a phone, where the drawer would cover the page.
      if (isAssistantPageMode && !isMobile) {
        useAssistantPanelStore.getState().setIsOpen(true);
      }

      if (chatId) {
        handleSendMessage(message);
      } else {
        onFirstMessage(message);
      }
    },
    [isAssistantPageMode, isMobile, chatId, handleSendMessage, onFirstMessage]
  );

  const composerRef = useRef<AiChatComposerRef>(null);
  const headerRef = useRef<HTMLDivElement>(null);

  // send a pendingMessage right away
  useEffect(() => {
    if (pendingMessage) {
      handleSendMessage(pendingMessage);
      clearPendingMessage();
    }
  }, [pendingMessage, handleSendMessage, clearPendingMessage]);

  // drop a prompt into the composer for the user to send
  useEffect(() => {
    if (composerDraft) {
      composerRef.current?.appendValue(composerDraft);
      clearComposerDraft();
    }
  }, [composerDraft, clearComposerDraft]);

  const handleExampleSelect = useCallback(
    (message: string, mode: ChatMode) => {
      setMode(mode);

      const composer = composerRef.current;

      if (composer) {
        composer.setValue(message);
        composer.focus();
      }
    },
    [setMode]
  );

  const handleReusePrompt = useCallback((text: string) => {
    composerRef.current?.appendValue(text);
    composerRef.current?.focus();
  }, []);

  const switchView = useAssistantPanelStore((state) => state.switchView);
  const { trackLearnModeOpened } = useAssistantPanelAnalytics();

  const isWide = useContainerBreakpoint(ASSISTANT_PANEL_CONTAINER, HISTORY_SIDE_PANEL_BREAKPOINT);

  const handleLearnView = useCallback(
    (source: AssistantPanelLearnModeSource) => {
      trackLearnModeOpened({ source });
      switchView(AssistantPanelView.LEARN);
    },
    [switchView, trackLearnModeOpened]
  );

  const isStreaming = chatId ? isChatStatusBusy(status) : false;

  const showLoadingIndicator = conversationLoading && messages.length === 0;
  const showWelcome = messages.length === 0 && !conversationLoading;
  const showNewChatButton = !!chatId || conversationLoading;

  return (
    <>
      <AssistantPanelLayoutHeader ref={headerRef}>
        <Box align="center" gap="medium" grow="1" className={styles.headerSide}>
          {!showTermsOverlay && conversations.length > 0 && (
            <AssistantPanelHistoryDropdown
              deleteChat={onDeleteChat}
              chats={conversations}
              loading={conversationsLoading}
              onSelectChat={onSelectChat}
              boundaryRef={headerRef}
            />
          )}

          {!showTermsOverlay &&
            showNewChatButton &&
            (isWide ? (
              <Button variant="secondary" size="small" startIcon={Plus} onPress={onNewChat}>
                New chat
              </Button>
            ) : (
              <ButtonIcon icon={Plus} variant="secondary" size="small" onPress={onNewChat}>
                New chat
              </ButtonIcon>
            ))}
        </Box>

        <Badge
          type="weak"
          variant={mode === ChatMode.BUILD ? "blue" : "gray"}
          startIcon={mode === ChatMode.BUILD ? Flash : MessageBubble}
          text={mode === ChatMode.BUILD ? "Build" : "Chat"}
        />

        <Box grow="1" justify="end" className={styles.headerSide} gap="medium">
          {!isAssistantPageMode && (
            <ButtonIcon
              icon={GraduationHat}
              variant="ghost"
              size="small"
              onPress={() => handleLearnView("chat_header")}
            >
              Switch to Learn mode
            </ButtonIcon>
          )}
          <AssistantPanelChatConversationActions chatId={chatId} onDeleteChat={onDeleteChat} />
          <AssistantPanelLayoutHeaderActions />
        </Box>
      </AssistantPanelLayoutHeader>
      {showTermsOverlay && <AssistantPanelChatTermsOverlay />}
      <Box
        direction="column"
        grow="1"
        justify={showWelcome ? "center" : "between"}
        fullWidth
        zeroMinHeight
        className={styles.content}
        inert={showTermsOverlay}
      >
        <Box direction="column" justify="center" align="center" zeroMinHeight>
          <LoadingIndicator loading={showLoadingIndicator} size="large" />
          {showWelcome && <AssistantPanelChatWelcome />}
          {messages.length > 0 && !showLoadingIndicator && (
            <AiChatMessages
              messages={messages}
              isStreaming={isStreaming}
              toolIcons={toolIcons}
              onApproveTool={handleApproveTool}
              onRejectTool={handleRejectTool}
              onReusePrompt={handleReusePrompt}
            />
          )}
        </Box>

        <Box direction="column">
          {beforeComposer}
          <AiChatComposer
            error={error}
            onSubmit={handleSubmit}
            onStop={chatId ? handleStop : noop}
            isStreaming={isStreaming}
            focusKey={focusKey}
            composerToolbarSlot={composerToolbarSlot}
            composerFooterSlot={composerFooterSlot}
            ref={composerRef}
          />

          {showWelcome && <ChatExamples onPromptSelect={handleExampleSelect} />}
        </Box>
      </Box>
    </>
  );
};

export default AssistantPanelChatContent;
